import {
  type OpportunitiesQuery,
  SubmissionAction,
  SubmissionState,
  useAddSubmissionRuleOutReasonMutation,
  useTransitionSubmissionMutation,
} from "@qw/graphql";
import { parseError, type VerboseError } from "@qw/qw-common";
import { FieldInput, FieldSelect, FieldTextarea, useModal, useToast } from "@qw/ui";
import { z } from "zod";
import { getSubmissionRepairOptions } from "./submission-repair";

export type RailSubmission = OpportunitiesQuery["opportunities"][number]["submissions"][number];

const marketLabel = ({ appetiteProduct }: RailSubmission) =>
  `${appetiteProduct.carrierName} - ${appetiteProduct.carrierProductName}`;

// Ported from web's submissions/actions/use-submission-actions so the rail fires the same
// mutations behind the same expectedState guard. refetchQueries is the only thing web couples
// to its own page query, so it comes in as a parameter.
export const useSubmissionActions = (refetchQueries: string[]) => {
  const [transition] = useTransitionSubmissionMutation();
  const [addRuleOutReason] = useAddSubmissionRuleOutReasonMutation();
  const { openForm } = useModal();
  const { toast } = useToast();

  // A rejected mutation (e.g. an InvalidTransitionState) would otherwise be swallowed by
  // the modal wrapper, so the broker sees nothing and assumes it "won't save".
  const notifyFailure = (title: string, error: unknown) =>
    toast({ title, description: parseError(error as VerboseError), variant: "destructive" });

  // For modal forms (opened with autoClose off): close only once the mutation resolves so
  // a rejection surfaces a toast and leaves the form open to retry, never closing as if saved.
  const submitOrNotify = async (close: () => void, failureTitle: string, run: () => Promise<unknown>) => {
    try {
      await run();
      close();
    } catch (error) {
      notifyFailure(failureTitle, error);
    }
  };

  const runTransition = (
    submission: RailSubmission,
    action: SubmissionAction,
    details?: string,
    repairToState?: SubmissionState
  ) =>
    transition({
      variables: {
        input: {
          id: submission.id,
          expectedState: submission.state,
          action,
          details,
          repairToState,
        },
      },
      refetchQueries,
    });

  // For actions fired straight off a menu item, where there is no form left open to hold:
  // report the failure and let the broker retry from the row.
  const handleTransition = async (submission: RailSubmission, action: SubmissionAction) => {
    try {
      await runTransition(submission, action);
    } catch (error) {
      notifyFailure("Couldn't update submission", error);
    }
  };

  // Web's version can also take a plain yes/no confirmation, but every caller in the monorepo
  // asks for a reason, so the rail only ports the reason form.
  const handleConfirmation = (submission: RailSubmission, action: SubmissionAction) =>
    openForm(<FieldInput name="details" label={marketLabel(submission)} placeholder="Reason" />, {
      title: "Confirm",
      defaultValues: { details: "" },
      validationSchema: z.object({ details: z.string().min(1, "Please enter a reason") }),
      autoClose: false,
      onSubmit: ({ details }, close) =>
        submitOrNotify(close, "Couldn't update submission", () => runTransition(submission, action, details)),
    });

  const handleAddRuleOutReason = (submission: RailSubmission) =>
    openForm(<FieldInput name="details" label={marketLabel(submission)} placeholder="Reason" />, {
      title: "Add rule out reason",
      defaultValues: { details: "" },
      validationSchema: z.object({ details: z.string().min(1, "Please enter a reason") }),
      autoClose: false,
      onSubmit: ({ details }, close) =>
        submitOrNotify(close, "Couldn't add rule out reason", () =>
          addRuleOutReason({
            variables: { input: { id: submission.id, details } },
            refetchQueries,
          })
        ),
    });

  const handleRepair = async (submission: RailSubmission) => {
    const repairOptions = getSubmissionRepairOptions(submission.state);
    if (repairOptions.length === 0) {
      return;
    }

    await openForm(
      <>
        <FieldSelect name="repairToState" label="Repair to state" options={repairOptions} />
        <FieldTextarea
          name="details"
          label="Repair note"
          placeholder="Optional context for why this repair is needed"
          rows={3}
        />
      </>,
      {
        title: "Repair submission state",
        defaultValues: { repairToState: repairOptions[0].value, details: "" },
        validationSchema: z.object({
          repairToState: z.nativeEnum(SubmissionState),
          details: z.string().optional(),
        }),
        submitButtonProps: { children: "Repair" },
        autoClose: false,
        onSubmit: ({ repairToState, details }, close) =>
          submitOrNotify(close, "Couldn't repair submission", () =>
            runTransition(submission, SubmissionAction.Revert, details || undefined, repairToState)
          ),
      }
    );
  };

  return {
    handleAddRuleOutReason,
    handleConfirmation,
    handleRepair,
    handleTransition,
  };
};
